/**
 * Shared timeline labeling for ManageAppointment, PatientAppointmentBooking, and the manage-calendar modal.
 * Half-hour grids often end at clinic close (e.g. 6:00 PM) while bookings may extend later; labels should
 * match Firestore start/end, not just the last visible grid cell.
 */

export function formatSlotRangeLabelFromMinutes(startMinutes, endMinutes) {
  const fmt = (minutes) => {
    const hours24 = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const ampm = hours24 >= 12 ? "PM" : "AM";
    const hours12 = hours24 % 12 || 12;
    return `${hours12}:${mins.toString().padStart(2, "0")} ${ampm}`;
  };
  return `${fmt(startMinutes)} - ${fmt(endMinutes)}`;
}

export function parseClockLabelToMinutes(timeStr) {
  if (!timeStr || typeof timeStr !== "string") return NaN;
  const t = timeStr.trim();
  if (/^\d{1,2}:\d{2}\s?(AM|PM)$/i.test(t)) {
    const [time, period] = t.split(/\s?(AM|PM)$/i);
    const [hours, minutes] = time.split(":").map(Number);
    let hour24 = hours;
    if (period.toUpperCase() === "PM" && hour24 !== 12) hour24 += 12;
    else if (period.toUpperCase() === "AM" && hour24 === 12) hour24 = 0;
    return hour24 * 60 + minutes;
  }
  const [hours, minutes] = t.split(":").map(Number);
  if (!Number.isFinite(hours)) return NaN;
  return hours * 60 + (Number.isFinite(minutes) ? minutes : 0);
}

/** Last calendar minute of the same day (11:59 PM). Minute 1440 is midnight / next day. */
export const LAST_MINUTE_OF_CALENDAR_DAY = 23 * 60 + 59;

/** Single clock label e.g. "10:25 PM" from minutes since midnight. */
export function formatClock12FromMinutes(minutes) {
  const hours24 = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const ampm = hours24 >= 12 ? "PM" : "AM";
  const hours12 = hours24 % 12 || 12;
  return `${hours12}:${mins.toString().padStart(2, "0")} ${ampm}`;
}

/** Normalize Firestore / form clock strings to 12-hour labels for notifications and UI. */
export function formatClockLabelForDisplay(timeStr) {
  if (timeStr == null || timeStr === "") return "";
  const minutes = parseClockLabelToMinutes(String(timeStr).trim());
  if (!Number.isFinite(minutes)) return String(timeStr).trim();
  return formatClock12FromMinutes(minutes);
}

export function clockLabelsEqual(a, b) {
  const ma = parseClockLabelToMinutes(a);
  const mb = parseClockLabelToMinutes(b);
  if (Number.isFinite(ma) && Number.isFinite(mb)) return ma === mb;
  return String(a ?? "").trim() === String(b ?? "").trim();
}

/** Normalize appointment.date to YYYY-MM-DD when present. */
export function normalizeAppointmentDateKey(raw) {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  const isoDay = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return isoDay ? isoDay[1] : s;
}

/** Records stored under appointments/{dateKey} belong to that day even if date is omitted. */
export function appointmentBelongsToDateDoc(appointment, dateKey) {
  if (!dateKey || !appointment) return false;
  const normalized = normalizeAppointmentDateKey(appointment.date);
  return !normalized || normalized === dateKey;
}

/** Ensure in-memory / post-read rows carry the parent document date for filters and compression. */
export function withAppointmentDocDate(appointment, dateKey) {
  if (!appointment || typeof appointment !== "object") return appointment;
  return {
    ...appointment,
    date: normalizeAppointmentDateKey(appointment.date) || dateKey,
  };
}

export function isCompressiblePendingStatus(status = "") {
  const n = (status || "").toString().trim().toLowerCase();
  return n === "pending" || n === "new";
}

/** Parse unavailableTimeSlots labels into minute ranges (requires a time parser). */
export function parseUnavailableLabelsToMinuteRanges(labels, parseMinutesFn) {
  if (!Array.isArray(labels) || typeof parseMinutesFn !== "function") {
    return [];
  }
  const ranges = [];
  for (const label of labels) {
    const parts = String(label || "")
      .split(" - ")
      .map((p) => p.trim());
    if (parts.length !== 2) continue;
    const start = parseMinutesFn(parts[0]);
    const end = parseMinutesFn(parts[1]);
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
      ranges.push({ start, end });
    }
  }
  return ranges.sort((a, b) => a.start - b.start);
}

export function mergeMinuteRanges(ranges) {
  if (!Array.isArray(ranges) || ranges.length === 0) return [];
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const out = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i += 1) {
    const cur = sorted[i];
    const last = out[out.length - 1];
    if (cur.start <= last.end) {
      last.end = Math.max(last.end, cur.end);
    } else {
      out.push({ ...cur });
    }
  }
  return out;
}

/**
 * Split [rangeStart, rangeEnd) into available vs blocked sub-ranges using persisted unavailable windows.
 */
export function splitRangeByOverlappingUnavailable(
  rangeStart,
  rangeEnd,
  unavailableRanges
) {
  if (
    typeof rangeStart !== "number" ||
    typeof rangeEnd !== "number" ||
    rangeEnd <= rangeStart
  ) {
    return [];
  }

  const clipped = mergeMinuteRanges(
    (unavailableRanges || [])
      .filter((r) => r && r.end > rangeStart && r.start < rangeEnd)
      .map((r) => ({
        start: Math.max(r.start, rangeStart),
        end: Math.min(r.end, rangeEnd),
      }))
  );

  if (clipped.length === 0) {
    return [{ start: rangeStart, end: rangeEnd, blocked: false }];
  }

  const segments = [];
  let cursor = rangeStart;
  for (const blocked of clipped) {
    if (blocked.start > cursor) {
      segments.push({ start: cursor, end: blocked.start, blocked: false });
    }
    segments.push({ start: blocked.start, end: blocked.end, blocked: true });
    cursor = Math.max(cursor, blocked.end);
  }
  if (cursor < rangeEnd) {
    segments.push({ start: cursor, end: rangeEnd, blocked: false });
  }
  return segments;
}

/**
 * Emit gap rows between appointments, carving out unavailable ranges so they stay visible
 * when a pending visit is moved later (e.g. 10:35–10:55 available, 10:55–11:25 blocked).
 */
export function forEachTimelineGapSlot({
  gapStart,
  gapEnd,
  unavailableRanges = [],
  slotInterval,
  officeEndTime,
  minSlotMinutes = 20,
  clampEnd = (_start, proposed) => proposed,
  formatRangeLabel = (s, e) => formatSlotRangeLabelFromMinutes(s, e),
  onSlot,
}) {
  if (typeof onSlot !== "function") return;
  if (
    typeof gapStart !== "number" ||
    typeof gapEnd !== "number" ||
    gapEnd <= gapStart
  ) {
    return;
  }

  const segments = splitRangeByOverlappingUnavailable(
    gapStart,
    gapEnd,
    unavailableRanges
  );

  for (const seg of segments) {
    let t = seg.start;
    const segEnd = Math.min(seg.end, gapEnd, officeEndTime);
    while (t < segEnd) {
      const chunkCap = Math.min(t + slotInterval, segEnd, officeEndTime);
      const end = clampEnd(t, chunkCap);
      if (end - t >= minSlotMinutes) {
        onSlot({
          start: t,
          end,
          timeStr: formatRangeLabel(t, end),
          blocked: seg.blocked,
        });
      }
      t = end;
    }
  }
}

/** Apply computeCompressibleTimelineShiftUpdates output to in-memory appointment rows (optimistic UI). */
export function applyTimelineShiftUpdatesToRows(appointments, chainUpdates) {
  if (!Array.isArray(appointments) || !chainUpdates?.length) {
    return appointments;
  }
  const patchById = new Map(chainUpdates.map((u) => [u.id, u]));
  return appointments.map((ap) => {
    const u = patchById.get(ap.id);
    if (!u) return ap;
    const startM = parseClockLabelToMinutes(u.startTime);
    const endM = parseClockLabelToMinutes(u.endTime);
    const next = {
      ...ap,
      startTime: u.startTime,
      endTime: u.endTime,
      time: u.startTime,
    };
    if (Number.isFinite(startM) && Number.isFinite(endM) && endM > startM) {
      next.duration = endM - startM;
    }
    return next;
  });
}

/**
 * Compression shifts needed for one appointments/{dateKey} document, grouped per dentist timeline.
 * @param {Set<string>|null} [options.dentistIdAllowlist] When set, only those dentist UIDs are included.
 */
export function computeDayTimelineCompressionShiftUpdates(
  appointments,
  formattedDate,
  { dentistIdAllowlist = null } = {}
) {
  const allUpdates = [];
  let exceedsDayEnd = false;
  if (!Array.isArray(appointments) || !formattedDate) {
    return { updates: allUpdates, exceedsDayEnd };
  }

  const byDentist = new Map();
  for (const ap of appointments) {
    if (!ap?.dentistId || !appointmentBelongsToDateDoc(ap, formattedDate)) continue;
    if (dentistIdAllowlist && !dentistIdAllowlist.has(ap.dentistId)) continue;
    const st = (ap.status || "").toLowerCase();
    if (st === "cancelled" || st === "canceled") continue;
    const row = withAppointmentDocDate(ap, formattedDate);
    if (!byDentist.has(row.dentistId)) byDentist.set(row.dentistId, []);
    byDentist.get(row.dentistId).push(row);
  }

  for (const timelineApps of byDentist.values()) {
    const sorted = [...timelineApps].sort((a, b) => {
      const ra = getAppointmentDisplayRangeMinutes(a);
      const rb = getAppointmentDisplayRangeMinutes(b);
      if (!ra || !rb) return 0;
      return ra.start - rb.start;
    });
    const { updates, exceedsDayEnd: dayEnd } = computeCompressibleTimelineShiftUpdates(
      sorted,
      {
        isCompressible: (apt, st) => isCompressiblePendingStatus(st || apt.status),
      }
    );
    if (dayEnd) exceedsDayEnd = true;
    allUpdates.push(...updates);
  }

  return { updates: allUpdates, exceedsDayEnd };
}

/** True when any row still needs stacking after the running tail. */
export function timelineShiftUpdatesAlreadyApplied(appointments, chainUpdates) {
  if (!chainUpdates?.length) return true;
  return chainUpdates.every((u) => {
    const ap = (appointments || []).find((row) => row.id === u.id);
    if (!ap) return false;
    return (
      clockLabelsEqual(ap.startTime, u.startTime) &&
      clockLabelsEqual(ap.endTime, u.endTime)
    );
  });
}

/**
 * Walk appointments in chronological order. Non-compressible visits (confirmed, reserved,
 * completed, …) anchor the timeline; compressible pendings that overlap the running tail are
 * stacked immediately after it — same chaining as booking compression precheck (not pairwise
 * adjacent fixes in sort order, which breaks when evening pendings sit before a shifted peer).
 *
 * @param {object[]} appointmentsSortedByStart Already sorted by display start ascending.
 * @param {(apt: object, statusLower: string) => boolean} options.isCompressible
 * @returns {{ updates: Array<{id: string, startTime: string, endTime: string, originalStart: string, originalEnd: string}>, exceedsDayEnd: boolean }}
 */
export function computeCompressibleTimelineShiftUpdates(
  appointmentsSortedByStart,
  { isCompressible, lastMinuteOfDay = LAST_MINUTE_OF_CALENDAR_DAY }
) {
  const updates = [];
  let exceedsDayEnd = false;
  let tailEnd = -Infinity;

  if (!Array.isArray(appointmentsSortedByStart)) {
    return { updates, exceedsDayEnd };
  }

  const compressFn =
    typeof isCompressible === "function"
      ? isCompressible
      : () => false;

  for (const apt of appointmentsSortedByStart) {
    if (!apt || typeof apt !== "object") continue;
    const st = (apt.status || "").toString().trim().toLowerCase();
    if (st === "cancelled" || st === "canceled") continue;

    const range = getAppointmentDisplayRangeMinutes(apt);
    if (!range || !Number.isFinite(range.start) || !Number.isFinite(range.end)) continue;
    const origStart = range.start;
    const origEnd = range.end;
    const duration = origEnd - origStart;
    if (duration < 1) continue;

    const movable = compressFn(apt, st);

    if (!movable) {
      tailEnd = Math.max(tailEnd, origEnd);
      continue;
    }

    let startM = origStart;
    let endM = origEnd;
    if (startM < tailEnd) {
      startM = tailEnd;
      endM = startM + duration;
    }

    if (endM > lastMinuteOfDay) {
      exceedsDayEnd = true;
    }

    tailEnd = Math.max(tailEnd, endM);

    const newStartStr = formatClock12FromMinutes(startM);
    const newEndStr = formatClock12FromMinutes(endM);
    const oldStartStr = formatClock12FromMinutes(origStart);
    const oldEndStr = formatClock12FromMinutes(origEnd);

    if (newStartStr !== oldStartStr || newEndStr !== oldEndStr) {
      updates.push({
        id: apt.id,
        startTime: newStartStr,
        endTime: newEndStr,
        originalStart: oldStartStr,
        originalEnd: oldEndStr,
      });
    }
  }

  return { updates, exceedsDayEnd };
}

/** Max duration (minutes) from start so start + duration does not exceed 11:59 PM the same day. */
export function maxDurationUntilMidnightFromSlotStart(startMinutes) {
  if (typeof startMinutes !== "number" || Number.isNaN(startMinutes)) return 0;
  const cap = LAST_MINUTE_OF_CALENDAR_DAY - startMinutes;
  return cap > 0 ? cap : 0;
}

/** Same stable key as legacy inline merge helpers (duplicate Firestore fragments). */
export function appointmentRecordKeyForSlotMerge(appointment) {
  if (!appointment || typeof appointment !== "object") return null;
  if (appointment.id != null && String(appointment.id) !== "") {
    return `id:${appointment.id}`;
  }
  const date = appointment.date != null ? String(appointment.date) : "";
  const pid = appointment.patientId != null ? String(appointment.patientId) : "";
  const uid = appointment.uid != null ? String(appointment.uid) : "";
  const st = appointment.startTime || appointment.time || "";
  const et = appointment.endTime || "";
  return `legacy:${date}:${pid}:${uid}:${st}:${et}`;
}

export function getAppointmentDisplayRangeMinutes(appointment) {
  if (!appointment) return null;
  let startM;
  let endM;
  if (appointment.startTime && appointment.endTime) {
    startM = parseClockLabelToMinutes(appointment.startTime);
    endM = parseClockLabelToMinutes(appointment.endTime);
  } else if (appointment.startTime) {
    startM = parseClockLabelToMinutes(appointment.startTime);
    if (!Number.isFinite(startM)) return null;
    endM = startM + (appointment.duration || 30);
  } else if (appointment.time) {
    startM = parseClockLabelToMinutes(appointment.time);
    if (!Number.isFinite(startM)) return null;
    endM = appointment.endTime
      ? parseClockLabelToMinutes(appointment.endTime)
      : startM + (appointment.duration || 30);
  } else {
    return null;
  }
  if (!Number.isFinite(startM) || !Number.isFinite(endM)) return null;
  return { start: startM, end: endM };
}

/**
 * Apply the appointment record's true start/end to a timeline row. Merged half-hour cells often span
 * past the real end (e.g. grid 11:00–12:30 vs Firestore 11:00–12:05); trimming to truth fixes Manage view labels.
 * Still shows visits that extend past the grid (truth end after last merged cell) via stored endTime/duration.
 */
export function alignBookedSlotTimelineRowToTruth(slot) {
  if (!slot?.appointment) return slot;
  const truth = getAppointmentDisplayRangeMinutes(slot.appointment);
  if (!truth) return slot;
  const next = { ...slot };
  next.start = truth.start;
  next.end = truth.end;
  const label = formatSlotRangeLabelFromMinutes(next.start, next.end);
  next.timeStr = label;
  if ("display" in next && next.display !== undefined) {
    next.display = label;
  }
  return next;
}

/**
 * Merge consecutive half-hour (or any) rows that carry the same appointment id, then widen labels to true bounds.
 * @param {object} [options]
 * @param {(slot: object) => boolean} [options.skipMergeForSlot] If true for either adjacent row, they are not merged.
 *   When true for a row, {@link alignBookedSlotTimelineRowToTruth} is also skipped so half-hour labels stay
 *   granular (e.g. roster mirror unavailable rows must not all show the bench patient's full span).
 */
export function mergeContiguousBookedTimelineRows(slots, options = {}) {
  if (!Array.isArray(slots) || slots.length === 0) return slots;
  const skipMergeForSlot =
    typeof options.skipMergeForSlot === "function" ? options.skipMergeForSlot : null;
  const sorted = [...slots].sort((a, b) => (a.start ?? 0) - (b.start ?? 0));
  const out = [];
  for (const slot of sorted) {
    const key = appointmentRecordKeyForSlotMerge(slot.appointment);
    const prev = out[out.length - 1];
    const prevKey = appointmentRecordKeyForSlotMerge(prev?.appointment);
    const skipMergeHere =
      skipMergeForSlot &&
      (skipMergeForSlot(slot) || (prev ? skipMergeForSlot(prev) : false));
    if (
      key &&
      prev &&
      prevKey === key &&
      !skipMergeHere &&
      Number.isFinite(prev.end) &&
      Number.isFinite(slot.start) &&
      prev.end === slot.start
    ) {
      prev.end = slot.end;
      prev.timeStr = formatSlotRangeLabelFromMinutes(prev.start, prev.end);
      continue;
    }
    out.push({ ...slot });
  }
  return out.map((row) =>
    skipMergeForSlot && skipMergeForSlot(row) ? row : alignBookedSlotTimelineRowToTruth(row)
  );
}

/**
 * For confirmation UIs: collapse consecutive selected half-hour labels that belong to one appointment
 * into a single row (true times from the booking record).
 */
export function consolidateContiguousSelectedSlotsByAppointment(
  selectedTimeStrs,
  resolveBookedAppointment,
  options = {}
) {
  const timelineGridEndMinutes =
    typeof options.timelineGridEndMinutes === "number"
      ? options.timelineGridEndMinutes
      : 18 * 60;
  if (!Array.isArray(selectedTimeStrs) || selectedTimeStrs.length === 0) return [];

  const parseRange = (label) => {
    const parts = (label || "").split(" - ").map((p) => p.trim());
    if (parts.length < 2) return null;
    const start = parseClockLabelToMinutes(parts[0]);
    const end = parseClockLabelToMinutes(parts[1]);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
    return { start, end };
  };

  const decorated = selectedTimeStrs.map((timeStr) => ({
    timeStr,
    range: parseRange(timeStr),
    appt:
      typeof resolveBookedAppointment === "function"
        ? resolveBookedAppointment(timeStr)
        : null,
  }));

  const validRows = decorated.filter((x) => x.range);
  const invalidTimeStrs = decorated.filter((x) => !x.range).map((x) => x.timeStr);

  const enriched = [...validRows].sort((a, b) => a.range.start - b.range.start);

  const groups = [];
  let cur = null;

  for (const row of enriched) {
    const key = appointmentRecordKeyForSlotMerge(row.appt);
    const canMerge =
      cur &&
      cur.key &&
      key &&
      cur.key === key &&
      cur.rangeEnd === row.range.start;

    if (canMerge) {
      cur.segments.push(row.timeStr);
      cur.rangeEnd = row.range.end;
      continue;
    }

    if (cur) groups.push(cur);
    cur = {
      key,
      segments: [row.timeStr],
      rangeStart: row.range.start,
      rangeEnd: row.range.end,
      appt: row.appt,
    };
  }
  if (cur) groups.push(cur);

  const consolidated = groups.map((g) => {
    const truth = g.appt ? getAppointmentDisplayRangeMinutes(g.appt) : null;
    let displayStart = g.rangeStart;
    let displayEnd = g.rangeEnd;
    if (truth) {
      // If the user selected through the last on-grid cell for this booking, show the real Firestore end
      // (e.g. 7:00 PM) even when the half-hour grid stops at clinic close (e.g. 6:00 PM).
      const gridAlignedApptEnd = Math.min(truth.end, timelineGridEndMinutes);
      if (g.rangeEnd >= gridAlignedApptEnd) {
        displayEnd = Math.max(g.rangeEnd, truth.end);
      }
    }
    const displayLabel = formatSlotRangeLabelFromMinutes(displayStart, displayEnd);
    return {
      segments: g.segments,
      displayLabel,
      bookedAppointment: g.appt,
      statusProbeSlot: g.segments[0],
    };
  });

  for (const label of invalidTimeStrs) {
    consolidated.push({
      segments: [label],
      displayLabel: label,
      bookedAppointment:
        typeof resolveBookedAppointment === "function"
          ? resolveBookedAppointment(label)
          : null,
      statusProbeSlot: label,
    });
  }

  return consolidated;
}
